import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaTimes, FaArrowRight, FaChevronLeft, FaChevronRight, FaEye, FaCommentAlt, FaColumns, FaArrowsAltH } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import pb from '../pocketbase';
import ComparisonSlider from '../components/ComparisonSlider';
import './AvantApresPage.css';

// Helper to get image URL using official SDK
const getFileUrl = (record, filename) => {
    if (!filename) return "";
    return pb.files.getUrl(record, filename);
};

const BANNER_IMG = "https://images.unsplash.com/photo-1507136566006-cfc505b114fc?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80";

const ComparisonRow = ({ project }) => {
    const [viewMode, setViewMode] = useState('slider'); // 'slider' or 'grid'
    const [currentPairIndex, setCurrentPairIndex] = useState(0);

    const listAvant = (project.liste_avant || "").split('\n').filter(Boolean);
    const listApres = (project.liste_apres || "").split('\n').filter(Boolean);

    const avantPhotos = Array.isArray(project.photo_avant) ? project.photo_avant : (project.photo_avant ? [project.photo_avant] : []);
    const apresPhotos = Array.isArray(project.photo_apres) ? project.photo_apres : (project.photo_apres ? [project.photo_apres] : []);

    const maxPairs = Math.min(avantPhotos.length, apresPhotos.length);

    const nextPair = () => setCurrentPairIndex((prev) => (prev + 1) % maxPairs);
    const prevPair = () => setCurrentPairIndex((prev) => (prev - 1 + maxPairs) % maxPairs);

    return (
        <div className="comparison-row-premium">
            <div className="comparison-header-premium">
                <div className="case-title-box-premium">
                    <span className="case-cat-badge-premium">
                        {project.categorie === 'auto' ? '🚗 AUTO' : '🛋️ MOBILIER'}
                    </span>
                    <h3>{project.titre}</h3>
                    {project.client_nom && <p className="case-client-name-premium">Réalisation : {project.client_nom}</p>}
                </div>
                <div className="display-toggles-premium">
                    <button className={`toggle-btn-premium ${viewMode === 'slider' ? 'active' : ''}`} onClick={() => setViewMode('slider')}>
                        <FaArrowsAltH /> <span>Interactif</span>
                    </button>
                    <button className={`toggle-btn-premium ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>
                        <FaColumns /> <span>Galerie</span>
                    </button>
                </div>
            </div>

            <div className="comparison-body-premium">
                {viewMode === 'slider' ? (
                    <div className="slider-area-navigation-premium">
                        <div className="slider-wrapper-main-premium">
                            {maxPairs > 0 ? (
                                <>
                                    <ComparisonSlider
                                        before={getFileUrl(project, avantPhotos[currentPairIndex])}
                                        after={getFileUrl(project, apresPhotos[currentPairIndex])}
                                    />
                                    {maxPairs > 1 && (
                                        <div className="slider-nav-arrows-premium">
                                            <button className="nav-arrow-premium prev" onClick={prevPair}><FaChevronLeft /></button>
                                            <button className="nav-arrow-premium next" onClick={nextPair}><FaChevronRight /></button>
                                        </div>
                                    )}
                                </>
                            ) : <div className="no-images-premium">Images en cours de chargement...</div>}
                        </div>

                        {maxPairs > 1 && (
                            <div className="thumbnail-selector-bar-premium">
                                {avantPhotos.slice(0, maxPairs).map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`thumb-item-premium ${currentPairIndex === idx ? 'active' : ''}`}
                                        onClick={() => setCurrentPairIndex(idx)}
                                        data-label={`Vue ${idx + 1}`}
                                    >
                                        <img src={getFileUrl(project, avantPhotos[idx])} alt={`Vue ${idx + 1}`} />
                                        <span className="thumb-vue-label">Vue {idx + 1}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="grid-comparison-premium">
                        {avantPhotos.slice(0, maxPairs).map((_, idx) => (
                            <div key={idx} className="grid-pair-row-premium" onClick={() => { setViewMode('slider'); setCurrentPairIndex(idx); }}>
                                <div className="grid-item-premium">
                                    <img src={getFileUrl(project, avantPhotos[idx])} alt="Avant" />
                                    <div className="label-aa-premium label-before">AVANT</div>
                                </div>
                                <div className="grid-item-premium">
                                    <img src={getFileUrl(project, apresPhotos[idx])} alt="Après" />
                                    <div className="label-aa-premium label-after">APRÈS</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="details-panel-premium">
                    <div className="details-section-premium problematic">
                        <h4><FaTimes /> Problématiques</h4>
                        <ul>
                            {listAvant.length > 0 ? listAvant.map((item, i) => (
                                <li key={i}>{item}</li>
                            )) : <li>État initial à restaurer</li>}
                        </ul>
                    </div>
                    <div className="details-section-premium solution">
                        <h4><FaCheck /> Solutions Apportées</h4>
                        <ul>
                            {listApres.length > 0 ? listApres.map((item, i) => (
                                <li key={i}>{item}</li>
                            )) : <li>Restauration complète effectuée</li>}
                        </ul>
                    </div>
                </div>
            </div>

            <div className="comparison-footer-premium">
                <div className="case-tags-premium">
                    {(project.tags || "").split(',').filter(Boolean).map((tag, i) => (
                        <span key={i} className="case-tag-premium">#{tag.trim()}</span>
                    ))}
                </div>
                <Link to={`/avant-apres/${project.id}`} className="btn-details-premium">
                    VOIR LES DÉTAILS <FaEye />
                </Link>
            </div>
        </div>
    );
};

const AvantApresPage = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('auto');
    const filters = [
        { id: 'auto', label: '🚗 Automobiles' },
        { id: 'mobilier', label: '🛋️ Mobiliers' }
    ];

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const records = await pb.collection('realisations').getFullList({
                sort: '-created'
            });
            setProjects(records);
        } catch (err) {
            console.error("Fetch Projects Error", err);
        } finally {
            setLoading(false);
        }
    };

    const filteredProjects = projects.filter(p => !activeFilter || p.categorie === activeFilter);

    return (
        <div className="prestations-page-wrapper">
            <section className="selection-trigger-section">
                <div className="container">
                    <motion.div className="selector-header center">
                        <span className="premium-label">NOS RÉALISATIONS</span>
                        <h2 className="section-title">AVANT / <span className="text-gradient-blue">APRÈS</span></h2>
                    </motion.div>

                    <div className="intro-content-premium text-center">
                        <span className="intro-badge-premium">LA PREUVE PAR L'IMAGE</span>
                        <h3 className="sub-header-premium">Un résultat <span className="text-gradient-blue">impeccable</span>, à chaque fois</h3>
                        <div className="intro-underline"></div>
                        <p className="description-text-premium">Découvrez l'étendue de notre savoir-faire à travers ces transformations concrètes.</p>

                        <div className="filter-bar-premium">
                            {filters.map(f => (
                                <button
                                    key={f.id}
                                    className={`filter-btn-premium ${activeFilter === f.id ? 'active' : ''}`}
                                    onClick={() => setActiveFilter(f.id)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="voiture-aa-content">
                <div className="container">
                    {loading ? (
                        <div className="text-center py-5">
                            <p>Chargement des réalisations...</p>
                        </div>
                    ) : filteredProjects.length > 0 ? (
                        filteredProjects.map(p => <ComparisonRow key={p.id} project={p} />)
                    ) : (
                        <div className="no-projects-premium text-center">
                            <p>Aucune réalisation dans cette catégorie pour le moment.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Footer CTA (Premium structure) */}
            <section className="voiture-footer-cta-premium">
                <div className="container">
                    <div className="cta-card-premium has-accent">
                        <div className="cta-glow-effect"></div>
                        <div className="cta-content-wrapper">
                            <div className="cta-left">
                                <div className="cta-icon-box-premium">
                                    <FaCommentAlt />
                                </div>
                                <div className="cta-text-content-premium">
                                    <h3>Convaincu par notre expertise ?</h3>
                                    <p>Redonnez à votre véhicule ou mobilier l'éclat qu'il mérite. Nos experts s'occupent de tout.</p>
                                </div>
                            </div>
                            <div className="cta-right">
                                <Link to="/reservation" className="btn-cta-premium">
                                    RÉSERVER MAINTENANT <FaArrowRight />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AvantApresPage;
